import {
  type DefaultError,
  type MutateOptions,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query";
import {
  assertRequestAuthority,
  captureRequestAuthority,
  type RequestAuthority,
} from "./authority";

type Intent<T> = { input: T; authority: RequestAuthority };

/** Capture at the UI invocation, before TanStack callbacks, retries or offline queueing yield. */
export function useAuthorityMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  client?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { onMutate, ...remaining } = options;
  const mutation = useMutation<TData, TError, Intent<TVariables>, TContext>(
    {
      ...remaining,
      mutationFn: (intent, context) => {
        assertRequestAuthority(intent.authority);
        if (!options.mutationFn)
          throw new Error("An authority-bound mutation function is required.");
        return options.mutationFn(intent.input, context);
      },
      ...(onMutate
        ? {
            onMutate: (
              intent: Intent<TVariables>,
              context: Parameters<NonNullable<typeof onMutate>>[1],
            ) => {
              assertRequestAuthority(intent.authority);
              return onMutate(intent.input, context);
            },
          }
        : {}),
      onSuccess: (data, intent, result, context) =>
        options.onSuccess?.(data, intent.input, result, context),
      onError: (error, intent, result, context) =>
        options.onError?.(error, intent.input, result, context),
      onSettled: (data, error, intent, result, context) =>
        options.onSettled?.(data, error, intent.input, result, context),
    },
    client,
  );
  const callbacks = (
    value?: MutateOptions<TData, TError, TVariables, TContext>,
  ): MutateOptions<TData, TError, Intent<TVariables>, TContext> => ({
    onSuccess: (data, intent, result, context) =>
      value?.onSuccess?.(data, intent.input, result, context),
    onError: (error, intent, result, context) =>
      value?.onError?.(error, intent.input, result, context),
    onSettled: (data, error, intent, result, context) =>
      value?.onSettled?.(data, error, intent.input, result, context),
  });
  return {
    ...mutation,
    variables: mutation.variables?.input,
    mutate: (input, value) =>
      mutation.mutate({ input, authority: captureRequestAuthority() }, callbacks(value)),
    mutateAsync: (input, value) =>
      mutation.mutateAsync({ input, authority: captureRequestAuthority() }, callbacks(value)),
  } as UseMutationResult<TData, TError, TVariables, TContext>;
}
